import { billingApi } from '../../../api/billingApi';
import { User } from '../../../types/user';
// Tipo que recibe del form
export interface ManualPaymentInput {
  amount: number;
  method: 'manual';
  receiptImageUrl: string;
  email?: string;
  whatsapp?: string;
  /** Periodo elegido (pro_monthly | pro_semiannual | pro_yearly). Sin él, el servidor cobra el plan vigente. */
  planId?: string | null;
}
export type PaymentStatus = 'pending' | 'completed' | 'failed' | 'cancelled' | 'refunded';
class PaymentController {
  // Catálogo LOCAL solo como plan por defecto de la navegación: el monto y los periodos reales
  // los manda el servidor (plan_options, 27-sep-2026) y la pantalla de pago los pisa.
  static readonly PLANS = {
    PRO_MONTHLY: {
      id: 'pro_monthly',
      name: 'Plan PRO (Mensual)',
      amount: 208,
      currency: 'MXN',
      duration: 30,
      description: 'Suscripción mensual al plan PRO de Lealify'
    },
    PRO_SEMIANNUAL: {
      id: 'pro_semiannual',
      name: 'Plan PRO (Semestral)',
      amount: 1250,
      currency: 'MXN',
      duration: 182,
      description: 'Suscripción semestral al plan PRO de Lealify'
    },
    PRO_YEARLY: {
      id: 'pro_yearly',
      name: 'Plan PRO (Anual)',
      amount: 2500,
      currency: 'MXN',
      duration: 365,
      description: 'Suscripción anual al plan PRO de Lealify (por sucursal)'
    }
  };
  static planById(id?: string | null) {
    return Object.values(PaymentController.PLANS).find((p) => p.id === id) || PaymentController.PLANS.PRO_YEARLY;
  }
  /**
   * Procesar pago manual - delega en el adaptador REST billingApi (lealify-base).
   * El comprobante ya viene subido (URL); aquí solo se registra el pago pendiente.
   */
  static async processManualPayment(_user: User, data: ManualPaymentInput): Promise<string> {
    try {
      const { receiptImageUrl, email, whatsapp, planId } = data;
      if (!receiptImageUrl) {
        throw new Error('Se requiere el comprobante de pago');
      }
      return await billingApi.processManual({ receiptImageUrl, email, whatsapp, planCode: planId ?? null });
    } catch (error) {
      console.error('Error processing manual payment:', error);
      throw error;
    }
  }
  /**
   * Procesar pago con BTCPay (checkout hospedado).
   * La creación del invoice ocurre server-side (lealify-base) porque requiere la
   * API key secreta del store. Devuelve el link de checkout al que se redirige al
   * usuario; la suscripción se activa luego vía webhook.
   */
  static async processBtcpayPayment(
    planId: string = 'pro_yearly'
  ): Promise<{ id: string; redirectUrl: string }> {
    const r = await billingApi.processBtcpay(planId);
    if (!r.checkoutLink) {
      throw new Error('BTCPay no devolvió un link de pago');
    }
    return { id: r.paymentId, redirectUrl: r.checkoutLink };
  }

  /**
   * Procesar pago con Mercado Pago (Checkout Pro, pago único).
   * La preference se crea server-side (lealify-base) con el access token secreto.
   * Devuelve el init_point al que se redirige al usuario; la suscripción se activa
   * luego vía webhook (igual que BTCPay).
   */
  static async processMercadoPagoPayment(
    planId: string = 'pro_yearly'
  ): Promise<{ id: string; redirectUrl: string }> {
    const r = await billingApi.processMercadoPago(planId);
    if (!r.initPoint) {
      throw new Error('Mercado Pago no devolvió un link de pago');
    }
    return { id: r.paymentId, redirectUrl: r.initPoint };
  }

  static async processPayment(
    method: string,
    user: User,
    data: ManualPaymentInput | any
  ): Promise<{ id: string; redirectUrl?: string }> {
    try {
      switch (method) {
        case 'manual':
          const paymentId = await this.processManualPayment(user, data as ManualPaymentInput);
          return { id: paymentId };
        case 'btcpay':
          return await this.processBtcpayPayment(data?.planId || 'pro_yearly');
        case 'mercadopago':
          return await this.processMercadoPagoPayment(data?.planId || 'pro_yearly');
        default:
          throw new Error(`Método desconocido: ${method}`);
      }
    } catch (error) {
      console.error(`Error processing ${method} payment:`, error);
      throw error;
    }
  }
}
export default PaymentController;
